import React from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from "@mui/material";
import { actions as MutationsActions } from "../../../../Ducks/Mutations.duck";
import { IntlMessage } from "../../../../../../components/Intl";
import { formatMutationType } from "../../../../../../utils/formatter/mutationFormatter";
import { formatName } from "../../../../../../utils/formatter/nameFormatter";
import { HasAccessTo, SearchInput } from "../../../../../../components";
import { GridItemStyled, GridStyled } from "./MutationsDialog.styles";
import { BATCH, EDIT } from "../../../../../../common/Authorization/permissions";
import MutationsTableSkeleton from "./MutationsTableSkeleton";
const MUTATIONS_DIALOG_TESTID = "mutations-dialog";
const MUTATIONS_SELECT_MUTATION_TYPE_TESTID = "mutations-select-mutation-type";
const MUTATIONS_SELECT_STATUS_TESTID = "mutations-select-status";
const MUTATIONS_CHECKBOX_SELECT_ALL_TESTID = "mutations-checkbox-select-all";
const MUTATIONS_CHECKBOX_ROW_TESTID = "mutations-checkbox-row";
const BTN_RESEND_MUTATIONS_TESTID = "btn-resend-mutations";
const BTN_DECLINE_MUTATIONS_TESTID = "btn-decline-mutations";
const BTN_CLOSE_MUTATIONS_DIALOG_TESTID = "btn-close-mutations-dialog";
const MutationsDialog = ({ open, connectionId, batchId, batchMongoId, handleClose, mutationTypes }) => {
  const dispatch = useDispatch();
  const { employerId } = useParams();
  const navigate = useNavigate();
  const { items, page, pageSize, totalCount, error, loading } = useSelector((state) => state.MutationsReducer);
  const [selectedMutations, setSelectedMutations] = React.useState([]);
  const handleToggleMutation = React.useCallback(
    (mutationExternalId) => {
      const alreadyExists = selectedMutations.find((item) => item === mutationExternalId);
      setSelectedMutations(
        alreadyExists ? selectedMutations.filter((item) => item !== mutationExternalId) : [...selectedMutations, mutationExternalId]
      );
    },
    [selectedMutations, setSelectedMutations]
  );
  React.useEffect(() => {
    dispatch(MutationsActions.clearMutations());
    dispatch(MutationsActions.setConnectionId(connectionId));
    dispatch(MutationsActions.setIds(batchId, batchMongoId));
    dispatch(MutationsActions.requestData());
  }, [connectionId, batchId, batchMongoId, dispatch]);
  const handlePageChange = React.useCallback(
    (mutation, newPage) => {
      dispatch(MutationsActions.changePage(newPage + 1));
    },
    [dispatch]
  );
  const handleRowsPerPageChange = React.useCallback(
    (mutation) => {
      dispatch(MutationsActions.setPageSize(mutation.target.value));
    },
    [dispatch]
  );
  const resendMutations = React.useCallback(() => {
    dispatch(MutationsActions.resendMutations(selectedMutations));
    navigate(`/employers/${employerId}/connections/${connectionId}/info`);
  }, [connectionId, dispatch, employerId, navigate, selectedMutations]);
  const declineMutations = React.useCallback(() => {
    dispatch(MutationsActions.declineMutations(selectedMutations));
    navigate(`/employers/${employerId}/connections/${connectionId}/info`);
  }, [connectionId, dispatch, employerId, navigate, selectedMutations]);
  const countSelectedMutations = React.useMemo(() => {
    return selectedMutations.length;
  }, [selectedMutations]);
  const hasAccessToBatchEdit = HasAccessTo(BATCH, EDIT);
  const isResendButtonDisabled = React.useMemo(() => {
    return selectedMutations.length === 0 || !hasAccessToBatchEdit;
  }, [hasAccessToBatchEdit, selectedMutations.length]);
  const isDeclineButtonDisabled = React.useMemo(() => {
    return selectedMutations.length === 0 || !hasAccessToBatchEdit;
  }, [hasAccessToBatchEdit, selectedMutations.length]);
  const handleEmployeeSearch = React.useCallback(
    (event) => {
      dispatch(MutationsActions.search(event.target.value));
    },
    [dispatch]
  );
  const handleMutationTypeChange = React.useCallback(
    (event) => {
      dispatch(MutationsActions.applyFilter("mutationType", event.target.value));
    },
    [dispatch]
  );
  const handleStatusChange = React.useCallback(
    (event) => {
      dispatch(MutationsActions.applyFilter("status", event.target.value));
    },
    [dispatch]
  );
  const areAllSelected = React.useMemo(() => {
    if (items.length === 0) return false;
    return items.every((mutation) => selectedMutations.includes(mutation.externalId));
  }, [items, selectedMutations]);
  const handleSelectAll = React.useCallback(() => {
    if (areAllSelected) {
      const currentPageIds = items.map((mutation) => mutation.externalId);
      setSelectedMutations(selectedMutations.filter((id) => !currentPageIds.includes(id)));
    } else {
      const currentPageIds = items.map((mutation) => mutation.externalId);
      const newSelections = [...selectedMutations];
      currentPageIds.forEach((id) => {
        if (!newSelections.includes(id)) {
          newSelections.push(id);
        }
      });
      setSelectedMutations(newSelections);
    }
  }, [areAllSelected, items, selectedMutations]);
  const mutationStatus = ["pending", "processing", "failed", "complete"];
  return !error && /* @__PURE__ */ React.createElement(
    Dialog,
    {
      open,
      onClose: () => handleClose(),
      fullWidth: true,
      maxWidth: "md",
      "aria-labelledby": "process-mutation-dialog",
      "data-testid": MUTATIONS_DIALOG_TESTID
    },
    /* @__PURE__ */ React.createElement(DialogTitle, { id: "process-mutation-dialog" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "connection.processes.mutationsNumber" })),
    /* @__PURE__ */ React.createElement(DialogContent, null, /* @__PURE__ */ React.createElement(GridStyled, { container: true, spacing: 3 }, /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 3 }, /* @__PURE__ */ React.createElement(
      Select,
      {
        onChange: handleMutationTypeChange,
        defaultValue: "''",
        fullWidth: true,
        "data-testid": MUTATIONS_SELECT_MUTATION_TYPE_TESTID
      },
      mutationTypes.map((mutationType) => /* @__PURE__ */ React.createElement(MenuItem, { key: mutationType, value: mutationType }, formatMutationType(mutationType))),
      /* @__PURE__ */ React.createElement(MenuItem, { key: "all", value: "''" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "all" }))
    )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 3 }, /* @__PURE__ */ React.createElement(
      Select,
      {
        onChange: handleStatusChange,
        defaultValue: "''",
        fullWidth: true,
        "data-testid": MUTATIONS_SELECT_STATUS_TESTID
      },
      /* @__PURE__ */ React.createElement(MenuItem, { value: "''" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "all" })),
      mutationStatus.map((status) => /* @__PURE__ */ React.createElement(MenuItem, { key: status, value: status }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: status })))
    )), /* @__PURE__ */ React.createElement(GridItemStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(SearchInput, { onChange: handleEmployeeSearch, initialSearchQuery: null }))), /* @__PURE__ */ React.createElement(Table, null, /* @__PURE__ */ React.createElement(TableHead, null, /* @__PURE__ */ React.createElement(TableRow, null, /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(
      Checkbox,
      {
        color: "primary",
        checked: areAllSelected,
        indeterminate: selectedMutations.length > 0 && !areAllSelected,
        onChange: handleSelectAll,
        "data-testid": MUTATIONS_CHECKBOX_SELECT_ALL_TESTID
      }
    )), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "mutation.type" })), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "events.content.header.hrEmployee" })), /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "employees.status" })))), /* @__PURE__ */ React.createElement(TableBody, null, loading ? /* @__PURE__ */ React.createElement(MutationsTableSkeleton, { rows: 10, columns: 4 }) : items.map((mutation) => /* @__PURE__ */ React.createElement(TableRow, { hover: true, key: mutation.externalId }, /* @__PURE__ */ React.createElement(TableCell, null, /* @__PURE__ */ React.createElement(
      Checkbox,
      {
        color: "primary",
        checked: selectedMutations.includes(mutation.externalId),
        onChange: () => handleToggleMutation(mutation.externalId),
        "data-testid": `${MUTATIONS_CHECKBOX_ROW_TESTID}-${mutation.externalId}`
      }
    )), /* @__PURE__ */ React.createElement(TableCell, null, formatMutationType(mutation.type)), /* @__PURE__ */ React.createElement(TableCell, null, formatName(mutation.employeeName[0])), /* @__PURE__ */ React.createElement(TableCell, null, mutation.syncStatus)))))),
    /* @__PURE__ */ React.createElement(DialogActions, null, /* @__PURE__ */ React.createElement(
      TablePagination,
      {
        component: "div",
        count: totalCount,
        onPageChange: handlePageChange,
        onRowsPerPageChange: handleRowsPerPageChange,
        page: page - 1,
        rowsPerPage: pageSize,
        rowsPerPageOptions: [100, 500, 1e3, 1e4]
      }
    ), /* @__PURE__ */ React.createElement(
      Button,
      {
        onClick: resendMutations,
        disabled: !!isResendButtonDisabled,
        "data-testid": BTN_RESEND_MUTATIONS_TESTID
      },
      /* @__PURE__ */ React.createElement(FormattedMessage, { id: "connection.processes.mutations.resend", values: { count: countSelectedMutations } })
    ), /* @__PURE__ */ React.createElement(
      Button,
      {
        onClick: declineMutations,
        disabled: !!isDeclineButtonDisabled,
        "data-testid": BTN_DECLINE_MUTATIONS_TESTID
      },
      /* @__PURE__ */ React.createElement(FormattedMessage, { id: "connection.processes.mutations.decline", values: { count: countSelectedMutations } })
    ), /* @__PURE__ */ React.createElement(Button, { onClick: () => handleClose(), color: "secondary", "data-testid": BTN_CLOSE_MUTATIONS_DIALOG_TESTID }, /* @__PURE__ */ React.createElement(IntlMessage, { value: "close" })))
  );
};
MutationsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  batchId: PropTypes.string.isRequired,
  batchMongoId: PropTypes.string.isRequired,
  connectionId: PropTypes.string.isRequired
};
export default MutationsDialog;
